import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useFormProgress } from '../../../hooks/useFormProgress';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { fetchApplicationDetails, saveFormData } from '../../../utils/apiUtils';
import styled from 'styled-components';
import FilePreviewModal from '../../common/FilePreviewModal';
import FormPageNavigator from '../../common/FormPageNavigator';
import SessionTimer from '../../common/SessionTimer';
import ApplicationIdDisplay from '../../common/ApplicationIdDisplay';
import { getSecureFileUrl } from '../../../utils/secureFileUtils';

const RAW_API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? 'https://backend.mngldodo.site' : 'http://localhost:5000');
const API_URL = RAW_API_URL.endsWith('/api') ? RAW_API_URL.slice(0, -4) : RAW_API_URL;

// A4 Dimensions
const A4_WIDTH_PX = 794;
const A4_HEIGHT_PX = 1123;

const ProgressBarContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 6px;
  background-color: #e0e0e0;
  z-index: 1000;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ProgressBarFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${props => `${props.progress}%`};
  background-color: #6cb288;
  transition: width 0.5s ease-in-out;
`;

const ProgressLabel = styled.div`
  position: fixed;
  top: 10px;
  right: 10px;
  background-color: #f8fcfa;
  color: #6cb288;
  border: 1px solid #a8d0b9;
  border-radius: 20px;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 500;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  z-index: 1000;
`;

const ZoomControlsContainer = styled.div`
  position: fixed;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 1000;
`;

const ZoomButton = styled.button`
  width: 30px;
  height: 30px;
  background-color: white;
  color: #6cb288;
  border: 1px solid #a8d0b9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
  padding: 0;
  
  &:hover {
    background-color: #f8fcfa;
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const OfflineIndicator = styled.div`
  background-color: #dc3545;
  color: white;
  text-align: center;
  padding: 10px;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 2000;
  font-size: 0.9em;
`;

const PageWrapper = styled.div<{ scale: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  min-height: 100vh;
  background-color: #f5f5f5;
  transform: scale(${props => props.scale});
  transform-origin: center top;
  transition: transform 0.3s ease;
`;

const A4PageContainer = styled.div`
  width: ${A4_WIDTH_PX}px;
  min-height: ${A4_HEIGHT_PX}px;
  background-color: white;
  padding: 50px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.07);
  box-sizing: border-box;
  position: relative;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: #212529;
  font-size: 13px;
  line-height: 1.6;
`;

const FloatingBackButton = styled.button`
  position: fixed;
  bottom: 30px;
  left: 30px;
  background: linear-gradient(145deg, #4a6cf7, #5e54e2);
  color: white;
  border: none;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(74, 108, 247, 0.3), 0 3px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  overflow: hidden;
  font-weight: bold;
  
  &:hover {
    transform: translateY(-5px) scale(1.05);
    box-shadow: 0 10px 20px rgba(74, 108, 247, 0.4), 0 6px 6px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }
`;

const FormContentContainer = styled.div``;

const FormFieldFullWidth = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
`;

const Label = styled.label`
  margin-right: 8px;
  white-space: nowrap;
  font-weight: 500;
  color: #495057;
  font-size: 0.9em;
  margin-bottom: 4px;
`;

const PageNumber = styled.div`
  position: absolute;
  bottom: 20px;
  right: 30px;
  font-size: 0.85em;
  color: #6c757d;
`;

const NextPageButton = styled.button`
  background-color: #6cb288;
  color: white;
  padding: 8px 25px;
  border: none;
  border-radius: 4px;
  font-size: 1em;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #4d9e70;
    transform: translateY(-2px);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
`;

const FileUploadButton = styled.button`
  background-color: #007bff;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 5px;
  font-size: 0.9em;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: #0056b3;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const DownloadButton = styled.button`
  background-color: #17a2b8;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 5px;
  font-size: 0.9em;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-right: 10px;

  &:hover:not(:disabled) {
    background-color: #138496;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const AppendixCard = styled.div`
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
`;

const AppendixTitle = styled.h3`
  color: #1a5276;
  font-size: 1.1em;
  margin: 0 0 15px 0;
  padding-bottom: 10px;
  border-bottom: 2px solid #3498db;
`;

const AppendixDescription = styled.p`
  color: #666;
  font-size: 0.9em;
  margin-bottom: 15px;
  line-height: 1.5;
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const UploadedFileName = styled.span`
  color: #28a745;
  font-size: 0.85em;
  margin-left: 10px;
  display: inline-block;
`;

interface PartnerDirectorDetail {
  nameInBlocks?: string;
  affidavitFile?: File | string | null;
  appendixBFile?: File | string | null;
  appendixCFile?: File | string | null;
  appendixDFile?: File | string | null;
  appendixEFile?: File | string | null;
  appendixFFile?: File | string | null;
}

interface FormData {
  applicationNoOfficeUse: string;
  nameInBlocks?: string; // Main applicant name
  dealershipType?: string;
  affidavitFile?: File | string | null; // Appendix A for main applicant
  appendixAFile?: File | string | null;
  appendixBFile?: File | string | null;
  appendixCFile?: File | string | null;
  appendixDFile?: File | string | null;
  appendixEFile?: File | string | null;
  appendixFFile?: File | string | null;
  partnerDirectorDetails?: PartnerDirectorDetail[];
  errors?: {};
}

interface ApplicationFormPageAProps {
  formData?: any;
  isViewMode?: boolean;
}

const ApplicationFormPageA: React.FC<ApplicationFormPageAProps> = ({ isViewMode = false, formData: propFormData }) => {
  const formRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Application lock status (locked after payment or manually locked by admin) - declare early
  const [isApplicationLocked, setIsApplicationLocked] = useState<boolean>(false);

  const [applicationId, setApplicationId] = useState<string>('');
  const [zoomScale, setZoomScale] = useState<number>(1.0);
  
  const [formData, setFormData] = useState<FormData>(propFormData || {
    applicationNoOfficeUse: '',
    appendixAFile: null,
    appendixBFile: null,
    appendixCFile: null,
    appendixDFile: null,
    appendixEFile: null,
    appendixFFile: null,
    errors: {}
  });

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveMessage, setSaveMessage] = useState<string>('Saved');
  const [showSaveIndicator, setShowSaveIndicator] = useState<boolean>(false);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [showBrochureModal, setShowBrochureModal] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalFileUrl, setModalFileUrl] = useState<string>('');
  const [modalFileName, setModalFileName] = useState<string>('');
  const [modalFileType, setModalFileType] = useState<string>('');
  
  // Appendix templates availability
  const [appendixTemplates, setAppendixTemplates] = useState<{
    appendixA?: string;
    appendixB?: string;
    appendixC?: string;
    appendixD?: string;
    appendixE?: string;
    appendixF?: string;
  }>({});

  // Appendix Logic - which appendixes are visible for the selected Application Type
  const [visibleAppendixes, setVisibleAppendixes] = useState<string[]>(['A', 'B', 'C', 'D', 'E', 'F']);
  const [dealershipType, setDealershipType] = useState<string>('');

  // Global progress bar (across Page1+2+3+4) — PageA is info-only, doesn't contribute
  const formProgress = useFormProgress(formData);

  // Fetch appendix templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await fetch(`${API_URL}/api/appendix-templates`);
        if (response.ok) {
          const data = await response.json();
          setAppendixTemplates(data);
        }
      } catch (error) {
        console.error('Error fetching appendix templates:', error);
      }
    };
    fetchTemplates();
  }, []);

  // Online/Offline detection
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Get application ID from location state or sessionStorage
  useEffect(() => {
    const stateAppId = location.state?.applicationId;
    const sessionAppId = sessionStorage.getItem('currentApplicationId');
    
    if (stateAppId) {
      setApplicationId(stateAppId);
      console.log('[PageA] Got applicationId from location.state:', stateAppId);
    } else if (sessionAppId) {
      setApplicationId(sessionAppId);
      console.log('[PageA] Got applicationId from sessionStorage:', sessionAppId);
    }
  }, [location.state]);

  // Effect to check if application is locked (after payment or manually locked by admin)
  useEffect(() => {
    const checkLockStatus = async () => {
      if (!applicationId) return;
      try {
        const response = await axios.get(`${API_URL}/api/settings/application-lock-status/${applicationId}`);
        if (response.data.success && response.data.isLocked) {
          setIsApplicationLocked(true);
          console.log(`[PageA] Application ${applicationId} is locked: ${response.data.reason}`);
        } else {
          setIsApplicationLocked(false);
        }
      } catch (error) {
        console.error('[PageA] Error checking application lock status:', error);
      }
    };
    checkLockStatus();
  }, [applicationId]);

  // Fetch existing data when applicationId is available
  useEffect(() => {
    const fetchExistingData = async () => {
      if (applicationId) {
        try {
          const existingData = await fetchApplicationDetails(applicationId);
          if (existingData && existingData.formData) {
            setFormData(prev => ({
              ...prev,
              ...existingData.formData,
              applicationNoOfficeUse: applicationId
            }));
            // Also get the dealershipType for appendix logic
            console.log('[PageA] Fetched dealershipType from application:', existingData.formData.dealershipType);
            if (existingData.formData.dealershipType) {
              setDealershipType(existingData.formData.dealershipType);
            }
          }
        } catch (error) {
          console.error('Error fetching existing data:', error);
        }
      }
    };
    fetchExistingData();
  }, [applicationId]);

  // Fetch appendix logic when dealershipType changes
  useEffect(() => {
    const fetchAppendixLogic = async () => {
      console.log('[PageA AppendixLogic] dealershipType:', dealershipType);
      if (!dealershipType) {
        setVisibleAppendixes(['A', 'B', 'C', 'D', 'E', 'F']); // Default - show all
        return;
      }
      
      try {
        const url = `${API_URL}/api/appendix-logic/${encodeURIComponent(dealershipType)}`;
        console.log('[PageA AppendixLogic] Fetching from:', url);
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          console.log('[PageA AppendixLogic] Response:', data);
          if (data.success && data.visibleAppendixes) {
            setVisibleAppendixes(data.visibleAppendixes);
            console.log('[PageA AppendixLogic] Set visibleAppendixes to:', data.visibleAppendixes);
          }
        }
      } catch (error) {
        console.error('Error fetching appendix logic:', error);
      }
    };
    fetchAppendixLogic();
  }, [dealershipType]);

  // Form progress is now calculated by the shared useFormProgress hook (global across Page1+2+3)
  // PageA is info-only and does NOT contribute to progress calculation

  // Debounced save function
  const saveToDatabaseCallback = useCallback(async (data: typeof formData, isDraft: boolean, anId?: string) => {
    const idToUse = anId || applicationId;
    if (!idToUse) return;
    
    setIsSaving(true);
    setSaveMessage('Saving...');
    setShowSaveIndicator(true);
    
    try {
      await saveFormData(idToUse, { ...data, currentPage: 'A' }, isDraft);
      setSaveMessage('Saved');
      setTimeout(() => setShowSaveIndicator(false), 2000);
    } catch (error) {
      console.error('Error saving data:', error);
      setSaveMessage('Save failed');
    } finally {
      setIsSaving(false);
    }
  }, [applicationId]);

  const debouncedSave = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (data: typeof formData) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          saveToDatabaseCallback(data, true, applicationId);
        }, 1500);
      };
    })(),
    [saveToDatabaseCallback, applicationId]
  );

  const handleFileUpload = (appendixKey: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      setFormData(prev => ({ ...prev, [appendixKey]: file }));
      setShowSaveIndicator(true);
      setSaveMessage('Saving...');
      debouncedSave({ ...formData, [appendixKey]: file });
    }
  };

  const handleDownloadTemplate = async (appendixType: string) => {
    const templateUrl = appendixTemplates[`appendix${appendixType}` as keyof typeof appendixTemplates];
    if (templateUrl) {
      try {
        const response = await fetch(`${API_URL}${templateUrl}`);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Appendix_${appendixType}_Template.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading template:', error);
        alert('Error downloading template. Please try again.');
      }
    } else {
      alert(`Appendix ${appendixType} template is not available yet. Please contact admin.`);
    }
  };

  const handleViewTemplate = async (appendixType: string) => {
    const templateUrl = appendixTemplates[`appendix${appendixType}` as keyof typeof appendixTemplates];
    if (!templateUrl) {
      alert(`Appendix ${appendixType} template is not available yet. Please contact admin.`);
      return;
    }

    try {
      // Clean up any previous blob URL
      if (modalFileUrl && modalFileUrl.startsWith('blob:')) {
        window.URL.revokeObjectURL(modalFileUrl);
      }

      const response = await fetch(`${API_URL}${templateUrl}`);
      if (!response.ok) {
        throw new Error('Failed to load template');
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);

      const lowerPath = String(templateUrl).toLowerCase();
      const isPdf = blob.type === 'application/pdf' || lowerPath.endsWith('.pdf');

      setModalFileUrl(objectUrl);
      setModalFileName(`Appendix_${appendixType}_Template${isPdf ? '.pdf' : ''}`);
      setModalFileType(isPdf ? 'application/pdf' : blob.type || '');
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error loading appendix template for preview:', error);
      alert('Error loading template for preview. Please try again or use Download Template.');
    }
  };

  const handleZoomIn = () => setZoomScale(prev => Math.min(prev + 0.1, 1.5));
  const handleZoomOut = () => setZoomScale(prev => Math.max(prev - 0.1, 0.5));
  const handleZoomReset = () => setZoomScale(1.0);

  const handleNextPage = () => {
    saveToDatabaseCallback(formData, true, applicationId);
    navigate('/form/page4', { state: { applicationId } });
  };

  const appendixList = [
    {
      key: 'A',
      title: 'APPENDIX - A',
      subtitle: 'AFFIDAVIT CUM UNDERTAKING',
      description: 'Affidavit-cum-Undertaking by Individual / Partner confirming submission of documents, financial commitments, acceptance of business risk, and adherence to MNGL terms.',
      fileKey: 'appendixAFile' as keyof FormData
    },
    {
      key: 'B',
      title: 'APPENDIX - B',
      subtitle: 'AFFIDAVIT FOR INDIVIDUAL & PARTNERS',
      description: 'Affidavit by Individual / Partner declaring eligibility, financial capability, and non-involvement in criminal or malpractices cases.',
      fileKey: 'appendixBFile' as keyof FormData
    },
    {
      key: 'C',
      title: 'APPENDIX - C',
      subtitle: 'AFFIDAVIT FOR NON-INDIVIDUAL ENTITY APPLICANT',
      description: 'Affidavit by Authorized Signatory of a Non-Individual Entity declaring authorization, legal status, eligibility, financial capability, and non-involvement in criminal or malpractices cases, and confirming compliance with MNGL requirements.',
      fileKey: 'appendixCFile' as keyof FormData
    },
    {
      key: 'D',
      title: 'APPENDIX - D',
      subtitle: 'AFFIDAVIT / UNDERTAKING OF OWNERSHIP OF PROPERTY / OFFERED LAND',
      description: 'Affidavit-cum-Undertaking by the Original Owner declaring ownership, clear and marketable title, encumbrance-free status, and consent for use of the offered land for MNGL DODO CNG Station.',
      fileKey: 'appendixDFile' as keyof FormData
    },
    {
      key: 'E',
      title: 'APPENDIX - E',
      subtitle: 'ADVOCATE\'S CERTIFICATE / LEGAL OPINION ON TITLE OF OFFERED LAND',
      description: 'Any additional declarations or undertakings as required. To be printed on Rs. 500 Stamp Paper and notarized.',
      fileKey: 'appendixEFile' as keyof FormData
    },
    {
      key: 'F',
      title: 'APPENDIX - F',
      subtitle: 'LAND OWNER CONSENT / NOC',
      description: 'Land owner consent or No Objection Certificate for use of land for CNG Station. To be printed on Rs. 500 Stamp Paper and notarized.',
      fileKey: 'appendixFFile' as keyof FormData
    }
  ];

  return (
    <>
      <ProgressBarContainer>
        <ProgressBarFill progress={formProgress} />
      </ProgressBarContainer>
      <ProgressLabel>Page A/5 • {Math.round(formProgress)}% complete</ProgressLabel>

      {!isOnline && <OfflineIndicator>You are offline. Changes will be saved when you reconnect.</OfflineIndicator>}
      
      {/* Application Locked Banner */}
      {isApplicationLocked && !isViewMode && (
        <div style={{
          position: 'fixed',
          top: '40px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(254, 243, 199, 0.92)',
          backdropFilter: 'blur(8px)',
          border: '2px solid #f59e0b',
          borderRadius: '12px',
          padding: '10px 20px',
          zIndex: 9998,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          boxShadow: '0 4px 20px rgba(245, 158, 11, 0.25)',
          maxWidth: '90%'
        }}>
          <span style={{ fontSize: '1.3rem' }}>🔒</span>
          <div>
            <div style={{ fontWeight: 600, color: '#92400e', fontSize: '0.9rem' }}>
              Application Locked
            </div>
            <div style={{ color: '#78350f', fontSize: '0.75rem' }}>
              This application is locked. You can view data but cannot make changes.
            </div>
          </div>
        </div>
      )}

      <ZoomControlsContainer>
        <ZoomButton onClick={handleZoomIn} title="Zoom In">+</ZoomButton>
        <ZoomButton onClick={handleZoomReset} title="Reset Zoom" style={{ fontSize: '12px' }}>⟳</ZoomButton>
        <ZoomButton onClick={handleZoomOut} title="Zoom Out">−</ZoomButton>
      </ZoomControlsContainer>

      <FloatingBackButton onClick={() => { window.scrollTo({ top: 0, behavior: 'auto' }); navigate('/form/page3', { state: { applicationId } }); }} title="Back to Page 3">
        ←
      </FloatingBackButton>

      {/* Home Icon Button - Above Save Icon */}
      <button
        onClick={() => navigate('/user-dashboard')}
        title="Go to Dashboard"
        style={{
          position: 'fixed',
          left: '20px',
          bottom: '150px',
          zIndex: 1000,
          height: '44px',
          padding: '0 16px 0 12px',
          borderRadius: '22px',
          border: 'none',
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          color: 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          fontSize: '14px',
          fontWeight: 600,
          boxShadow: '0 4px 14px rgba(99, 102, 241, 0.35)',
          transition: 'all 0.25s ease'
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
          <polyline points="9 22 9 12 15 12 15 22"/>
        </svg>
        Home
      </button>

      {/* Save Icon Button - Above Back Button - Hidden when application is locked */}
      {!isViewMode && !isApplicationLocked && (
        <button
          onClick={() => saveToDatabaseCallback(formData, true, applicationId)}
          disabled={isSaving || !applicationId || !isOnline}
          title="Save Draft"
          style={{
            position: 'fixed',
            left: '20px',
            bottom: '98px',
            zIndex: 1000,
            height: '44px',
            padding: '0 16px 0 12px',
            borderRadius: '22px',
            border: 'none',
            background: isSaving ? '#86efac' : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
            color: 'white',
            cursor: isSaving || !applicationId || !isOnline ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: 600,
            boxShadow: '0 4px 14px rgba(34, 197, 94, 0.35)',
            transition: 'all 0.25s ease',
            opacity: isSaving || !applicationId || !isOnline ? 0.6 : 1
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
            <polyline points="17 21 17 13 7 13 7 21"/>
            <polyline points="7 3 7 8 15 8"/>
          </svg>
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      )}

      <div style={{ position: 'fixed', top: '80px', left: '30px', zIndex: 1001 }}>
        <button 
          onClick={() => setShowBrochureModal(true)}
          style={{ 
            backgroundColor: '#2196f3', 
            color: '#ffffff', 
            borderRadius: '4px', 
            padding: '8px 15px',
            border: '1px solid #1976d2',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}
        >
          📄 View Brochure
        </button>
      </div>

      <FormPageNavigator currentPage={'A' as any} totalPages={14} basePath="/form/page" targetFormRef={formRef} zoomScale={zoomScale} />

      {showSaveIndicator && (
        <div style={{ position: 'fixed', top: '50px', left: '20px', backgroundColor: isSaving ? '#ffc107' : '#28a745', color: isSaving ? '#212529' : 'white', padding: '8px 16px', borderRadius: '20px', fontSize: '0.85em', zIndex: 1001, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
          {saveMessage}
        </div>
      )}

      <div style={{ position: 'fixed', bottom: '30px', right: '30px', zIndex: 1001 }}>
        <NextPageButton 
          onClick={handleNextPage} 
          style={{ 
            height: '35px', 
            width: '140px', 
            borderRadius: '4px', 
            whiteSpace: 'nowrap',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: '0 10px'
          }}
        >
          Next Page →
        </NextPageButton>
      </div>
      {/* WhatsApp Help Button */}
      <div style={{ position: 'fixed', right: '20px', bottom: '100px', zIndex: 999, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
        <span style={{ fontSize: '11px', fontWeight: 600, color: '#25D366', textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>Need Help!</span>
        <a 
          href="https://web.whatsapp.com/send?phone=919971874909&text=Hi%2C%20Need%20Help%20filling%20MNGL%20DODO%20form"
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'block', cursor: 'pointer', transition: 'transform 0.2s ease' }}
          onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
          title="Chat with us on WhatsApp"
        >
          <img src="/assets/images/whatsapp.png" alt="WhatsApp Help" style={{ }} />
        </a>
      </div>
      
      <PageWrapper scale={zoomScale}>
        <A4PageContainer ref={formRef}>
          <FormContentContainer>
            <FormFieldFullWidth style={{ marginBottom: '25px' }}>
              <Label style={{ fontWeight: 700, color: '#1a5276', display: 'block', fontSize: '1.3em', textAlign: 'center', textDecoration: 'underline', marginBottom: '10px' }}>
                APPENDIX DOCUMENTS
              </Label>
              <p style={{ textAlign: 'center', color: '#666', fontSize: '0.9em', margin: '0 0 20px 0' }}>
                This page shows the status of all required Appendix documents uploaded from Page 1.
                <br />
                All documents must be printed on Rs. 500 Stamp Paper and duly notarized before uploading.
              </p>
            </FormFieldFullWidth>

            {appendixList.filter(appendix => visibleAppendixes.includes(appendix.key)).map((appendix) => {
              // Determine upload status for each appendix - show ALL required uploaders (uploaded + missing)
              const getAppendixUploadStatus = (): { label: string; fileUrl: string; uploaded: boolean }[] => {
                const mainApplicantName = formData.nameInBlocks || 'Main Applicant';
                const partners = formData.partnerDirectorDetails || [];
                const isPartnership = formData.dealershipType === 'Partnership' || formData.dealershipType === 'LLP';
                const statuses: { label: string; fileUrl: string; uploaded: boolean }[] = [];
                
                // Check main applicant upload (Appendix A uses affidavitFile, others use appendixXFile)
                const mainAppendixField = appendix.key === 'A' ? 'affidavitFile' : `appendix${appendix.key}File`;
                const mainFileValue = (formData as any)[mainAppendixField];
                const mainFileUrl = mainFileValue && typeof mainFileValue === 'string' ? mainFileValue : '';
                // Always show main applicant status for Partnership/LLP types for Appendix A and B
                if (isPartnership && (appendix.key === 'A' || appendix.key === 'B')) {
                  statuses.push({ 
                    label: `Partner #1 (${mainApplicantName})`, 
                    fileUrl: mainFileUrl,
                    uploaded: !!mainFileValue
                  });
                } else if (mainFileValue) {
                  // For non-partnership or other appendices, only show if uploaded
                  statuses.push({ 
                    label: `Main Applicant (${mainApplicantName})`, 
                    fileUrl: mainFileUrl,
                    uploaded: true
                  });
                }
                
                // Check partner/director uploads - show ALL partners for Partnership/LLP for Appendix A and B
                partners.forEach((partner, idx) => {
                  const partnerAppendixField = appendix.key === 'A' ? 'affidavitFile' : `appendix${appendix.key}File`;
                  const partnerFileValue = (partner as any)[partnerAppendixField];
                  const partnerLabel = isPartnership ? `Partner #${idx + 2}` : `Director #${idx + 2}`;
                  const fileUrl = partnerFileValue && typeof partnerFileValue === 'string' ? partnerFileValue : '';
                  
                  // For Partnership/LLP and Appendix A/B, always show partner status
                  if (isPartnership && (appendix.key === 'A' || appendix.key === 'B')) {
                    statuses.push({ 
                      label: `${partnerLabel} (${partner.nameInBlocks || 'N/A'})`, 
                      fileUrl,
                      uploaded: !!partnerFileValue
                    });
                  } else if (partnerFileValue) {
                    // For other appendices, only show if uploaded
                    statuses.push({ 
                      label: `${partnerLabel} (${partner.nameInBlocks || 'N/A'})`, 
                      fileUrl,
                      uploaded: true
                    });
                  }
                });
                
                return statuses;
              };
              
              const statuses = getAppendixUploadStatus();
              const uploadedCount = statuses.filter(s => s.uploaded).length;
              const totalRequired = statuses.length;
              const allUploaded = uploadedCount === totalRequired && totalRequired > 0;
              const hasAnyUpload = uploadedCount > 0;
              
              // Handle view uploaded file - uses signed URLs for security
              const handleViewUploadedFile = async (fileUrl: string, uploaderLabel: string) => {
                if (!fileUrl) return;
                
                try {
                  // Get secure signed URL for the file
                  const secureUrl = await getSecureFileUrl(fileUrl, applicationId);
                  if (!secureUrl) {
                    console.error('[PageA] Failed to get secure URL for:', fileUrl);
                    alert('Unable to access file. Please try again.');
                    return;
                  }
                  
                  const fileExt = fileUrl.split('.').pop()?.toLowerCase() || '';
                  setModalFileUrl(secureUrl);
                  setModalFileName(`Appendix-${appendix.key} - ${uploaderLabel}`);
                  setModalFileType(fileExt === 'pdf' ? 'application/pdf' : `image/${fileExt}`);
                  setIsModalOpen(true);
                } catch (error) {
                  console.error('[PageA] Error getting secure URL:', error);
                  alert('Unable to access file. Please try again.');
                }
              };
              
              return (
                <AppendixCard key={appendix.key} style={{ borderColor: allUploaded ? '#27ae60' : (hasAnyUpload ? '#f39c12' : '#e74c3c') }}>
                  <AppendixTitle>
                    {appendix.title}
                    <span style={{ fontWeight: 400, fontSize: '0.85em', marginLeft: '10px', color: '#555' }}>
                      ({appendix.subtitle})
                    </span>
                  </AppendixTitle>
                  <AppendixDescription>{appendix.description}</AppendixDescription>
                  
                  {/* Upload Status Section */}
                  <div style={{ marginTop: '15px' }}>
                    {statuses.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {statuses.map((status, idx) => (
                          <div 
                            key={idx} 
                            style={{ 
                              padding: '10px 14px', 
                              backgroundColor: status.uploaded ? '#e8f8f0' : '#fdedec', 
                              borderRadius: '6px', 
                              border: `1px solid ${status.uploaded ? '#27ae60' : '#e74c3c'}`,
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between',
                              gap: '12px'
                            }}
                          >
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '8px',
                              color: status.uploaded ? '#1e8449' : '#c0392b',
                              fontSize: '0.95em',
                              fontWeight: 500,
                              flex: 1
                            }}>
                              <span style={{ color: status.uploaded ? '#27ae60' : '#e74c3c', fontSize: '1.1em' }}>
                                {status.uploaded ? '✓' : '✗'}
                              </span>
                              {status.uploaded 
                                ? `Via Appendix-${appendix.key} by ${status.label}`
                                : `Missing from ${status.label}`
                              }
                            </div>
                            {status.uploaded && status.fileUrl && (
                              <button
                                onClick={() => handleViewUploadedFile(status.fileUrl, status.label)}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  padding: '6px 14px',
                                  backgroundColor: '#3498db',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '0.85em',
                                  fontWeight: 500,
                                  cursor: 'pointer',
                                  transition: 'background-color 0.2s',
                                  whiteSpace: 'nowrap'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2980b9'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3498db'}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                  <circle cx="12" cy="12" r="3"></circle>
                                </svg>
                                View
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ 
                        padding: '12px 16px', 
                        backgroundColor: '#fdedec', 
                        borderRadius: '8px', 
                        border: '1px solid #e74c3c',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span style={{ color: '#e74c3c', fontSize: '1.1em' }}>✗</span>
                        <span style={{ color: '#c0392b', fontSize: '0.95em', fontWeight: 500 }}>
                          Appendix-{appendix.key} not uploaded. Please upload from Page 1.
                        </span>
                      </div>
                    )}
                  </div>
                </AppendixCard>
              );
            })}

            <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107' }}>
              <p style={{ margin: 0, fontSize: '0.9em', color: '#856404' }}>
                <strong>Important:</strong> All appendix documents must be printed on Rs. 500 Non-Judicial Stamp Paper, 
                properly filled, signed, and notarized before scanning and uploading. Incomplete or improper documentation 
                may result in rejection of your application.
              </p>
            </div>
          </FormContentContainer>
          <PageNumber>A</PageNumber>
        </A4PageContainer>
        
        <FilePreviewModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          fileUrl={modalFileUrl}
          fileName={modalFileName}
          fileType={modalFileType}
        />
      </PageWrapper>
      
      {!isViewMode && (
        <SessionTimer 
          sessionDuration={30}
          warningTime={5}
          onSessionExpired={() => navigate('/')}
          onSaveState={async () => {
            if (applicationId) {
              await saveToDatabaseCallback(formData, true, applicationId);
            }
          }}
        />
      )}

      {/* Application ID Display */}
      {applicationId && <ApplicationIdDisplay applicationId={applicationId} />}

      {/* Brochure PDF Modal */}
      {showBrochureModal && (
        <div 
          onClick={() => setShowBrochureModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 2000
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: '90vw',
              maxWidth: '900px',
              height: '85vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#f8fafc'
            }}>
              <h3 style={{ margin: 0, color: '#1e40af', fontWeight: 600 }}>📄 MNGL DODO Brochure</h3>
              <button
                onClick={() => setShowBrochureModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '4px 8px',
                  borderRadius: '4px'
                }}
              >
                ✕
              </button>
            </div>
            <iframe
              src="/brochures/mngl-dodo-brochure.pdf"
              style={{
                flex: 1,
                width: '100%',
                border: 'none'
              }}
              title="MNGL DODO Brochure"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ApplicationFormPageA;
